import './mobile-menu.scss';
import { ReactComponent as MobileCloseIcon } from '@/assets/icons/svg/common/mobile-close-icon.svg';
import { ReactComponent as MainLogo } from '@/assets/icons/svg/common/main-logo.svg';
import { ReactComponent as ArrowDownIcon } from '@/assets/icons/svg/common/arrow-down-icon.svg';
import { ReactComponent as InquiryArrow } from '@/assets/icons/svg/common/inquiry-arrow.svg';
import classNames from 'classnames';
import { menuListType } from './header';
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import paths from '@/routes/paths';

type Props = {
	mobileMenuOpen: boolean;
	setMobileMenuOpen: (open: boolean) => void;
	menuList: menuListType[];
	returnFocus: () => void;
};
export default function MobileMenu({
	mobileMenuOpen,
	setMobileMenuOpen,
	menuList,
	returnFocus
}: Props) {
	const location = useLocation();
	const [openMenu, setOpenMenu] = useState<number | null>(
		location.pathname.startsWith('/content/') ? 3 : null
	);
	const panel = useRef<HTMLDivElement>(null);
	const restoreFocus = useRef(returnFocus);
	useEffect(() => {
		if (!mobileMenuOpen) return;
		const overflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		panel.current
			?.querySelector<HTMLButtonElement>('.mobile-close-button')
			?.focus();
		return () => {
			document.body.style.overflow = overflow;
			restoreFocus.current();
		};
	}, [mobileMenuOpen]);
	const close = () => setMobileMenuOpen(false);
	return (
		<div
			id="mobile-navigation"
			ref={panel}
			className={classNames('mobile-header-menu-box', {
				open: mobileMenuOpen
			})}
			hidden={!mobileMenuOpen}
			role="dialog"
			aria-modal="true"
			aria-label="전체 메뉴"
			onKeyDown={(event) => {
				if (event.key === 'Escape') {
					event.stopPropagation();
					close();
				}
				if (event.key === 'Tab') {
					const elements = Array.from(
						panel.current?.querySelectorAll<HTMLElement>(
							'a[href],button:not([disabled])'
						) || []
					).filter((el) => el.getClientRects().length > 0);
					const first = elements[0],
						last = elements[elements.length - 1];
					if (event.shiftKey && document.activeElement === first) {
						event.preventDefault();
						last?.focus();
					} else if (
						!event.shiftKey &&
						document.activeElement === last
					) {
						event.preventDefault();
						first?.focus();
					}
				}
			}}
		>
			<div className="mobile-header-top">
				<Link
					to="/"
					onClick={close}
					data-analytics-cta="mobile_home"
					data-analytics-label="홈으로 이동"
					aria-label="미스터디벨로 홈으로 이동"
				>
					<MainLogo className="main-logo" />
				</Link>
				<button
					type="button"
					className="mobile-close-button"
					aria-label="메뉴 닫기"
					onClick={close}
				>
					<MobileCloseIcon className="mobile-close-icon" />
				</button>
			</div>
			<nav className="mobile-menu" aria-label="모바일 주 메뉴">
				<ul className="mobile-menu-list">
					{menuList.map((menu) => {
						const active =
							location.pathname === menu.path ||
							location.pathname.startsWith(`${menu.path}/`) ||
							(menu.id === 3 &&
								location.pathname === paths.column);
						const open = openMenu === menu.id;
						return (
							<li
								key={menu.id}
								className={classNames('mobile-menu-item', {
									open
								})}
							>
								{menu.subMenu ? (
									<button
										type="button"
										aria-expanded={open}
										aria-controls={`mobile-submenu-${menu.id}`}
										className={classNames('menu-label', {
											open,
											active
										})}
										onClick={() =>
											setOpenMenu(open ? null : menu.id)
										}
									>
										<span className="menu-label-text">
											{menu.label}
										</span>
										<ArrowDownIcon aria-hidden="true" />
									</button>
								) : (
									<Link
										to={menu.path}
										className={classNames('menu-label', {
											active
										})}
										onClick={close}
										data-analytics-cta={`mobile_menu_${menu.id}`}
										data-analytics-label={menu.label}
									>
										<span className="menu-label-text">
											{menu.label}
										</span>
									</Link>
								)}
								{menu.subMenu && (
									<ul
										id={`mobile-submenu-${menu.id}`}
										hidden={!open}
										className={classNames(
											'mobile-sub-menu-list',
											{ open }
										)}
									>
										{menu.subMenu.map((sub) => (
											<li
												key={sub.id}
												className={classNames(
													'mobile-sub-menu-item',
													{
														active:
															location.pathname ===
															sub.path
													}
												)}
											>
												<Link
													to={sub.path}
													onClick={close}
													className="mobile-sub-menu-text"
													aria-current={
														location.pathname ===
														sub.path
															? 'page'
															: undefined
													}
													data-analytics-cta={`mobile_submenu_${sub.id}`}
													data-analytics-label={
														sub.label
													}
												>
													{sub.label}
												</Link>
											</li>
										))}
									</ul>
								)}
							</li>
						);
					})}
				</ul>
			</nav>
			<Link
				to={paths.inquiry}
				onClick={close}
				className="mobile-menu-inquiry"
				data-analytics-cta="mobile_inquiry"
				data-analytics-label="모바일 문의하기"
			>
				<p>문의하기</p>
				<InquiryArrow className="inquiry-arrow" />
			</Link>
		</div>
	);
}
